import React, { useState } from "react";
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import {
  Loader2, LogIn, LogOut, Calendar, Users, ClipboardList,
  KeyRound, ShieldCheck, ShieldAlert, DollarSign, Receipt, Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

import AdminBookings from "@/pages/admin/AdminBookings";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPassword from "@/pages/admin/AdminPassword";
import AdminPayroll from "@/pages/admin/AdminPayroll";
import AdminExpenses from "@/pages/admin/AdminExpenses";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AdminInbox from "@/pages/admin/AdminInbox";

function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/adminconsole");
    } catch (err) {
      toast.error("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink px-6">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(138,138,138,0.08) 0%, transparent 60%)" }}
      />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="card-luxe p-10 w-full max-w-md relative z-10"
        data-testid="admin-login-form"
      >
        <p className="overline mb-3">Restricted Access</p>
        <h1 className="font-serif text-3xl mb-2">Admin Console</h1>
        <p className="text-white/50 text-sm mb-8">
          Sign in with your Nisky Auto admin credentials.
        </p>
        <div className="space-y-5 mb-8">
          <div>
            <label className="block mb-2 text-xs tracking-[0.25em] uppercase text-white/50">Email</label>
            <input type="email" className="input-luxe" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" data-testid="admin-login-email" />
          </div>
          <div>
            <label className="block mb-2 text-xs tracking-[0.25em] uppercase text-white/50">Password</label>
            <input type="password" className="input-luxe" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" data-testid="admin-login-password" />
          </div>
        </div>
        <button type="submit" className="btn-primary w-full justify-center" disabled={loading} data-testid="admin-login-submit">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />}
          Sign In
        </button>
        <p className="text-[10px] text-white/30 tracking-wider uppercase mt-6 text-center">
          Owner & Admins · Niskayuna NY
        </p>
      </motion.form>
    </div>
  );
}

function AdminLayout({ children }) {
  const { user, role, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const links = [
    { to: "/adminconsole",          label: "Bookings",  icon: ClipboardList, end: true },
    { to: "/adminconsole/calendar", label: "Calendar",  icon: Calendar },
    { to: "/adminconsole/payroll",  label: "Payroll",   icon: DollarSign },
    { to: "/adminconsole/expenses", label: "Expenses",  icon: Receipt },
    { to: "/adminconsole/users",    label: "Users",     icon: Users },
    { to: "/adminconsole/security", label: "Security",  icon: ShieldAlert },
    { to: "/adminconsole/inbox",    label: "Inbox",     icon: Inbox },
    { to: "/adminconsole/password", label: "Password",  icon: KeyRound },
  ];

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/adminconsole");
  };

  return (
    <div className="h-screen bg-ink text-white flex overflow-hidden">
      <aside className="w-64 p-6 hidden md:flex flex-col shrink-0" data-testid="admin-sidebar">
        <Link
          to="/"
          className="font-serif text-2xl mb-1"
          style={{ background: "linear-gradient(135deg,#8A8A8A 0%,#AAAAAA 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}
        >
          Nisky Auto
        </Link>
        <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-10">Admin Console</p>

        <nav className="flex flex-col gap-1 flex-1">
          {links.map((l) => {
            const Icon = l.icon;
            const active = l.end ? pathname === l.to : pathname.startsWith(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`admin-nav-${l.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 text-sm transition-all ${
                  active
                    ? "text-gold bg-gold/5"
                    : "text-white/60 hover:text-white"
                }`}
              >
                <Icon size={16} /> {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto pt-8">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={12} className="text-gold" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{role}</p>
          </div>
          <p className="text-xs text-white/70 mb-4 truncate" data-testid="admin-current-email">{user?.email}</p>
          <button
            onClick={handleLogout}
            className="btn-secondary w-full justify-center"
            style={{ padding: "10px 16px", fontSize: "11px" }}
            data-testid="admin-logout-button"
          >
            <LogOut size={12} /> Sign Out
          </button>
          <div className="flex justify-center gap-4 mt-3">
            <Link to="/terms" className="text-[10px] hover:opacity-70 transition-opacity" style={{ color: "rgba(255,255,255,0.22)" }}>Terms</Link>
            <span style={{ color: "rgba(255,255,255,0.15)", fontSize: 10 }}>·</span>
            <Link to="/privacy" className="text-[10px] hover:opacity-70 transition-opacity" style={{ color: "rgba(255,255,255,0.22)" }}>Privacy Policy</Link>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-elevated z-50 p-4 flex justify-between items-center">
        <span className="font-serif text-xl text-gold">Nisky · Admin</span>
        <button onClick={handleLogout} className="text-xs text-white/70" data-testid="admin-logout-button-mobile">Sign Out</button>
      </div>

      <main className="flex-1 p-6 md:p-10 pt-20 md:pt-10 overflow-y-auto overflow-x-hidden admin-page">
        {/* Mobile nav */}
        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto">
          {links.map((l) => {
            const active = l.end ? pathname === l.to : pathname.startsWith(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3 py-2 text-xs whitespace-nowrap ${
                  active ? "text-gold" : "text-white/60"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </div>
        {children}
      </main>
    </div>
  );
}

export default function AdminConsole() {
  const { user, loading, role } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink" data-testid="admin-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
    );
  }

  if (!user || !role) {
    return <AdminLogin />;
  }

  return (
    <AdminLayout>
      <Routes>
        <Route index element={<AdminBookings />} />
        <Route path="calendar" element={<AdminCalendar />} />
        <Route path="payroll" element={<AdminPayroll />} />
        <Route path="expenses" element={<AdminExpenses />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="security" element={<AdminSecurity />} />
        <Route path="inbox" element={<AdminInbox />} />
        <Route path="password" element={<AdminPassword />} />
        <Route path="*" element={<Navigate to="/adminconsole" replace />} />} />
      </Routes>
    </AdminLayout>
  );
}
